import { useMemo, useState, type FormEvent } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { setupPasswordWithToken } from "@/api/users";
import { BrandMark } from "@/components/BrandMark";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";

export function SetupPasswordPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => setupPasswordWithToken(token, password),
    onSuccess: () => setDone(true),
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } }).response
        ?.data?.detail;
      setError(detail || "error");
    },
  });

  const mismatch = useMemo(
    () => confirm.length > 0 && password !== confirm,
    [password, confirm],
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mismatch) {
      setError("password_mismatch");
      return;
    }
    if (password.length < 4) {
      setError("password_too_short");
      return;
    }
    mutation.mutate();
  };

  const bg = theme.palette.mode === "dark" ? "/brand/bg-dark.svg" : "/brand/bg-light.svg";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        px: 2,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ position: "absolute", top: 16, right: 16 }}>
        <LocaleSwitcher />
        <ThemeSwitcher />
      </Stack>

      <Card
        sx={{
          width: "100%",
          maxWidth: 440,
          backdropFilter: "blur(8px)",
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(30, 30, 30, 0.85)"
              : "rgba(255, 255, 255, 0.92)",
          borderColor: "divider",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <Stack alignItems="center" spacing={1.5}>
              <BrandMark variant="wordmark" height={56} />
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                {t("setupPassword.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {t("setupPassword.subtitle")}
              </Typography>
            </Stack>

            {!token && (
              <Alert severity="error">{t("setupPassword.missingToken")}</Alert>
            )}

            {done ? (
              <Stack spacing={2}>
                <Alert severity="success">{t("setupPassword.done")}</Alert>
                <Button
                  variant="contained"
                  onClick={() => navigate("/login", { replace: true })}
                >
                  {t("setupPassword.goToLogin")}
                </Button>
              </Stack>
            ) : (
              <Box component="form" onSubmit={onSubmit}>
                <Stack spacing={2}>
                  {error && (
                    <Alert severity="error">
                      {t(`setupPassword.err.${error}`, { defaultValue: error })}
                    </Alert>
                  )}
                  <TextField
                    label={t("setupPassword.password")}
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    autoFocus
                    autoComplete="new-password"
                    disabled={!token || mutation.isPending}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPwd((v) => !v)}
                              edge="end"
                              size="small"
                            >
                              {showPwd ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <TextField
                    label={t("setupPassword.confirm")}
                    type={showPwd ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    fullWidth
                    autoComplete="new-password"
                    error={mismatch}
                    helperText={mismatch ? t("setupPassword.err.password_mismatch") : ""}
                    disabled={!token || mutation.isPending}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!token || !password || mutation.isPending}
                    sx={{ py: 1.2 }}
                  >
                    {mutation.isPending ? t("common.loading") : t("setupPassword.submit")}
                  </Button>
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
